import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Tv, Trash2, Edit, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { apiClient } from '@/services/api';
import { usePlanFeature } from '@/hooks/usePlanFeature';
import { timeAgo, formatDateTime } from '@/utils/cn';

export function TvDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<any>({});
  const [pendingPlaylistId, setPendingPlaylistId] = useState<number | null>(null);

  const { data: tv } = useQuery({
    queryKey: ['tv', id],
    queryFn: () => apiClient.getTv(Number(id)).then(r => {
      setForm(r.data);
      return r.data;
    }),
    enabled: !!id,
  });
  const { data: playlists = [] } = useQuery({
    queryKey: ['playlists'],
    queryFn: () => apiClient.getPlaylists().then(r => r.data),
  });
  const { data: stores = [] } = useQuery({
    queryKey: ['stores'],
    queryFn: () => apiClient.getStores().then(r => r.data),
  });
  const { enabled: multiStore } = usePlanFeature('multi_store');
  // Mesmo sem multi-loja: se já existe mais de uma loja (ex.: tenant migrado),
  // mostramos o seletor pra reorganização.
  const showStoreField = multiStore || stores.length > 1;

  const updateMut = useMutation({
    mutationFn: (data: any) => apiClient.updateTv(Number(id), data),
    onSuccess: () => {
      toast.success('TV atualizada');
      qc.invalidateQueries({ queryKey: ['tv', id] });
      qc.invalidateQueries({ queryKey: ['tvs'] });
      setEditing(false);
    },
    onError: () => toast.error('Erro ao atualizar TV'),
  });

  // Sincroniza o valor do dropdown com o servidor sempre que o tv muda — assim, após
  // salvar (ou cancelar uma seleção), o select volta ao estado real.
  useEffect(() => {
    setPendingPlaylistId(tv?.current_playlist_id ?? null);
  }, [tv?.current_playlist_id]);

  const assignMut = useMutation({
    mutationFn: (playlistId: number) => apiClient.assignPlaylistToTv(Number(id), playlistId),
    onSuccess: () => {
      toast.success('Playlist atribuída');
      qc.invalidateQueries({ queryKey: ['tv', id] });
    },
    onError: () => {
      toast.error('Erro ao salvar playlist');
      // Reseta o select pra o valor real do servidor.
      setPendingPlaylistId(tv?.current_playlist_id ?? null);
    },
  });

  const deleteMut = useMutation({
    mutationFn: () => apiClient.deleteTv(Number(id)),
    onSuccess: () => {
      toast.success('TV removida');
      // Invalida a lista (e o detalhe) ANTES de navegar — sem isso, /tvs renderiza
      // do cache mostrando a TV recém-deletada até o próximo refetch automático
      // (forçando o usuário a dar F5).
      qc.invalidateQueries({ queryKey: ['tvs'] });
      qc.removeQueries({ queryKey: ['tv', id] });
      navigate('/tvs');
    },
    onError: () => toast.error('Erro ao remover TV'),
  });

  if (!tv) return <div className="p-8">Carregando…</div>;

  return (
    <div className="container-app py-8">
      <Link to="/tvs" className="inline-flex items-center gap-1 text-sm text-muted-foreground mb-4 hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Voltar
      </Link>

      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="grid place-items-center h-12 w-12 rounded-md bg-brand-100 text-brand-700">
            <Tv className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">{tv.name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant={tv.status === 'online' ? 'success' : tv.status === 'offline' ? 'danger' : 'warning'}>
                {tv.status}
              </Badge>
              <span className="text-sm text-muted-foreground">{tv.location}</span>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {editing ? (
            <>
              <Button variant="outline" onClick={() => { setForm(tv); setEditing(false); }}>Cancelar</Button>
              <Button onClick={() => updateMut.mutate(form)} loading={updateMut.isPending}><Save className="h-4 w-4" />Salvar</Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setEditing(true)}><Edit className="h-4 w-4" />Editar</Button>
              <Button variant="destructive" onClick={() => confirm('Remover esta TV?') && deleteMut.mutate()}><Trash2 className="h-4 w-4" /></Button>
            </>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mt-8">
        <Card className="md:col-span-2">
          <CardHeader><CardTitle>Informações</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <Field label="Nome" value={editing ? <Input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} /> : tv.name} />
            <Field label="Localização" value={editing ? <Input value={form.location || ''} onChange={e => setForm({ ...form, location: e.target.value })} /> : tv.location} />
            {showStoreField && (
              <Field
                label="Loja"
                value={editing ? (
                  <select
                    value={form.store_id ?? ''}
                    onChange={e => setForm({ ...form, store_id: e.target.value ? Number(e.target.value) : null })}
                    className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  >
                    <option value="">— sem loja —</option>
                    {stores.map((s: any) => (
                      <option key={s.id} value={s.id}>{s.name} (#{s.code}){s.is_default ? ' • default' : ''}</option>
                    ))}
                  </select>
                ) : (
                  storeLabel(stores, tv.store_id)
                )}
              />
            )}
            <Field label="Device ID" value={<code className="text-xs">{tv.device_id}</code>} />
            <Field label="Resolução" value={tv.resolution_width ? `${tv.resolution_width}×${tv.resolution_height}` : '-'} />
            <Field label="Pareada em" value={formatDateTime(tv.paired_at)} />
            <Field label="Última atividade" value={timeAgo(tv.last_seen_at)} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Playlist atual</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <select
              value={pendingPlaylistId ?? ''}
              onChange={e => setPendingPlaylistId(e.target.value ? Number(e.target.value) : null)}
              disabled={assignMut.isPending}
              className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm disabled:opacity-60"
            >
              <option value="">Selecione uma playlist…</option>
              {playlists.map((p: any) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            {pendingPlaylistId !== (tv.current_playlist_id ?? null) && (
              <Button
                onClick={() => pendingPlaylistId != null && assignMut.mutate(pendingPlaylistId)}
                disabled={pendingPlaylistId == null || assignMut.isPending}
                loading={assignMut.isPending}
                className="w-full"
                size="sm"
              >
                <Save className="h-4 w-4" />Salvar playlist
              </Button>
            )}
            {tv.current_playlist_id && (
              <Button asChild variant="outline" className="w-full" size="sm">
                <Link to={`/playlists/${tv.current_playlist_id}`}>Editar playlist</Link>
              </Button>
            )}
          </CardContent>
        </Card>

        <DeviceInfoCard hardwareInfo={tv.hardware_info} lastIp={tv.last_ip} />
      </div>
    </div>
  );
}

/**
 * Card com info do aparelho (marca, modelo, OS, RAM, versão do app) reportada
 * pelo tv-app no register e em cada heartbeat. Útil pro suporte rastrear
 * problemas — ex: Box X tem RAM Y e roda Android Z.
 */
function DeviceInfoCard({ hardwareInfo, lastIp }: { hardwareInfo: any; lastIp?: string | null }) {
  const hw = parseHardwareInfo(hardwareInfo);
  if (!hw && !lastIp) return null;
  return (
    <Card className="md:col-span-3">
      <CardHeader><CardTitle>Dispositivo</CardTitle></CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4">
          <Field label="Marca" value={hw?.brand || hw?.manufacturer || '-'} />
          <Field label="Modelo" value={hw?.model || hw?.device_name || hw?.product_name || '-'} />
          <Field label="Sistema" value={hw?.os ? `${hw.os}${hw.os_version ? ' ' + hw.os_version : ''}` : '-'} />
          <Field label="API Level" value={hw?.platform_api_level ?? '-'} />
          <Field label="Memória RAM" value={formatBytes(hw?.total_memory)} />
          <Field label="Versão do app" value={hw?.app_version ? `${hw.app_version}${hw?.app_build ? ` (${hw.app_build})` : ''}` : '-'} />
          <Field label="IP atual" value={lastIp || '-'} />
          <Field label="Build do SO" value={hw?.os_build_id || hw?.os_internal_build_id || '-'} />
          <Field label="Reportado em" value={hw?.reported_at ? formatDateTime(hw.reported_at) : '-'} />
        </div>
      </CardContent>
    </Card>
  );
}

function parseHardwareInfo(raw: unknown): Record<string, any> | null {
  if (!raw) return null;
  if (typeof raw === 'object') return raw as Record<string, any>;
  if (typeof raw === 'string') {
    try { return JSON.parse(raw); } catch { return null; }
  }
  return null;
}

function formatBytes(n: unknown): string {
  if (typeof n !== 'number' || !isFinite(n) || n <= 0) return '-';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 10 ? 0 : 1)} ${units[i]}`;
}

function storeLabel(stores: any[], storeId: number | null | undefined) {
  if (!storeId) return '—';
  const s = stores.find(x => x.id === storeId);
  return s ? `${s.name} (#${s.code})` : `#${storeId}`;
}

function Field({ label, value }: { label: string; value: any }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-sm font-medium mt-0.5">{value}</div>
    </div>
  );
}
